import { ExerciseDb } from 'lib/cosmos/exercise/exerciseDb.js';
import { SessionDb } from 'lib/cosmos/session/sessionDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { roundHelper } from 'lib/helpers/round.helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { TagType } from 'lib/ui/tag/index.js';

interface IComplianceItem {
	result: SessionDb.SessionStateResult;
}

export class SessionsHelper {
	getStateItems(data: {
		workout: WorkoutDb.IWorkoutItem;
		availableExercises: readonly ExerciseDb.IExerciseItem[];
	}): readonly SessionDb.ISessionStateItem[] {
		const stateItems: SessionDb.ISessionStateItem[] = [];

		if (data.workout) {
			if (data.workout.type === 'workout') {
				let containerIndex: number = 0;
				for (const exerciseContainer of data.workout.exerciseContainers) {
					containerIndex++;
					let variantIndex: number = 0;
					for (const workoutExercise of exerciseContainer.exercises) {
						const exercise = data.availableExercises.find((m) => m.id === workoutExercise.exerciseId);
						const variantChar = stringHelper.getAlphabetCharFromIndex(variantIndex);

						stateItems.push({
							variantId: exerciseContainer.id,
							id: workoutExercise.id,
							enableCompliance: false,
							variantsCount: exerciseContainer.exercises.length,
							variantChar: variantChar,
							items: workoutExercise.sets.map((set) => {
								const item: SessionDb.ISessionStateNestedItem = {
									id: set.id,
									title: set.text ?? '',
									result: 'n/a',
									enableCompliance: true
								};

								return item;
							}),
							title: exercise?.system.name ?? 'Session',
							indexAndVariant:
								exerciseContainer.exercises.length > 1
									? `${containerIndex}${variantChar}`
									: `${containerIndex}`
						});
						variantIndex++;
					}
				}
			} else if (data.workout.type === 'activity') {
				stateItems.push({
					enableCompliance: false,
					id: 'activityMain',
					title: 'Activity',
					items: [
						{
							enableCompliance: true,
							id: 'performedActivity',
							result: 'n/a',
							title: 'Successfully performed activity'
						}
					],
					variantChar: '',
					variantId: '',
					variantsCount: 1,
					indexAndVariant: undefined
				});
			}
		}

		return stateItems;
	}

	getComplianceTagType(compliance: number): TagType {
		if (compliance >= 80) {
			return 'success';
		}

		if (compliance >= 50) {
			return 'basic';
		}

		return 'fats';
	}

	localizeSessionType(sessionType: SessionDb.SessionType): string {
		if (sessionType === 'inPersonMeeting') {
			return 'In person meeting';
		}
		if (sessionType === 'offlineConsultation') {
			return 'Offline consultation';
		}
		if (sessionType === 'onlineConsultation') {
			return 'Online consultation';
		}
		if (sessionType === 'workout') {
			return 'Workout';
		}

		throw Error(`Unsupported session type '${sessionType}'`);
	}

	calculateComplianceScore(session: SessionDb.ISessionItem): number {
		const complianceItems: IComplianceItem[] = [];

		for (const item of session.state) {
			for (const nestedItem of item.items) {
				if (nestedItem.enableCompliance) {
					if (nestedItem.result !== 'n/a') {
						// only calculate compliance from set items
						complianceItems.push({
							result: nestedItem.result
						});
					}
				}
			}
		}

		const totalItems: number = complianceItems.length;
		const maxScore: number = 100;
		const scorePerItem: number = maxScore / totalItems;
		const completedItems: number = complianceItems.filter((m) => m.result === 'completed').length;
		const score = scorePerItem * completedItems;

		return roundHelper.roundTo(score, 1);
	}
}

export const sessionsHelper = new SessionsHelper();
